import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export abstract class ApiService {
  protected readonly http = inject(HttpClient);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);

  protected extractError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object') {
        const errors = (body as { errors?: string[] }).errors;
        if (Array.isArray(errors) && errors.length) return errors[0];
        const msg = (body as { message?: string }).message;
        if (typeof msg === 'string' && msg) return msg;
      }
    }
    return '';
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  private add(
    severity: 'success' | 'error' | 'warn',
    summary: string,
    detail: string,
    life = 3000,
  ) {
    this.messages.add({ severity, summary, detail, life });
  }

  showSuccess(detail?: string) {
    this.add('success', this.t('API.TOAST.SUCCESS'), detail ?? this.t('API.TOAST.SAVED'));
  }

  showError(detail?: string) {
    this.add('error', this.t('API.TOAST.ERROR'), detail ?? this.t('API.TOAST.ERROR'), 5000);
  }

  showWarn(detail: string) {
    this.add('warn', this.t('API.TOAST.WARN'), detail, 4000);
  }

  showLoadError(err?: unknown) {
    const detail = err ? this.extractError(err) : '';
    this.add('error', this.t('API.TOAST.ERROR'), detail || this.t('API.TOAST.LOAD_ERROR'), 5000);
  }

  showCreateSuccess(detail?: string) {
    this.add('success', this.t('API.TOAST.SUCCESS'), detail ?? this.t('API.TOAST.CREATED'));
  }

  showCreateError(err?: unknown) {
    const detail = err ? this.extractError(err) : '';
    this.add('error', this.t('API.TOAST.ERROR'), detail || this.t('API.TOAST.CREATE_ERROR'), 5000);
  }

  showUpdateSuccess(detail?: string) {
    this.add('success', this.t('API.TOAST.SUCCESS'), detail ?? this.t('API.TOAST.UPDATED'));
  }

  showUpdateError(err?: unknown) {
    const detail = err ? this.extractError(err) : '';
    this.add('error', this.t('API.TOAST.ERROR'), detail || this.t('API.TOAST.UPDATE_ERROR'), 5000);
  }

  showDeleteSuccess(detail?: string) {
    this.add('success', this.t('API.TOAST.SUCCESS'), detail ?? this.t('API.TOAST.DELETED'));
  }

  showDeleteError(err?: unknown) {
    const detail = err ? this.extractError(err) : '';
    this.add('error', this.t('API.TOAST.ERROR'), detail || this.t('API.TOAST.DELETE_ERROR'), 5000);
  }
}
